'use client'

import { useContext, useMemo } from 'react'

import { Cell, Icon, Bubble } from '@vinted/web-ui'
import { LocationPinMap24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import {
  DistanceUnitEnum,
  DropOffsPointType,
  DropOffType,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import {
  GetShippingLabelContext,
  useDropOffPointMapContext,
} from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { AddressModel } from '@marketplace-web/address/address-data'

type Props = {
  onDropOffPointOpen: () => void
  address?: AddressModel | null
}

const DropOffPoints = ({ onDropOffPointOpen, address }: Props) => {
  const translate = useTranslate('conversation.get_shipping_label.modal')
  const { selectedDropOffPoint, nearbyDropOffPoints } = useDropOffPointMapContext()
  const {
    state: {
      labelOptions: { labelOptions, labelOptionsRewrite },
      dropOffPointTypeSelection: { selectedDropOffPointType },
      dropOffTypeSelection: { selectedDropOffTypeKey },
    },
  } = useContext(GetShippingLabelContext)

  const isDropOffMapEnabled = useMemo(() => {
    return labelOptionsRewrite
      ? labelOptionsRewrite?.dropOffs?.some(dropOff => dropOff.providesNearbyDropOffPoints)
      : labelOptions?.dropOffMapEnabled
  }, [labelOptions, labelOptionsRewrite])

  const isHomePickupSelected = useMemo(
    () =>
      selectedDropOffPointType === DropOffsPointType.Home ||
      selectedDropOffTypeKey === DropOffType.Home,
    [selectedDropOffPointType, selectedDropOffTypeKey],
  )

  const renderDropOffPoints = useMemo(() => {
    if (!isDropOffMapEnabled || isHomePickupSelected) return null

    const getDistanceToNearestDropOffPoint = () => {
      if (!selectedDropOffPoint) return null

      const { distance, distanceUnit } = selectedDropOffPoint

      if (!distanceUnit || (distance == null && distance !== 0)) return null

      if (distanceUnit === DistanceUnitEnum.Kilometers || distanceUnit === DistanceUnitEnum.Miles) {
        return `${distance.toFixed(1)} ${distanceUnit}`
      }

      return `${Math.round(distance)} ${distanceUnit}`
    }

    const isEmptyState = !address || !nearbyDropOffPoints?.length

    const title = isEmptyState
      ? translate('drop_off_points.drop_off_points_title')
      : translate('drop_off_points.nearest_drop_off_point_title')

    const body =
      !isEmptyState &&
      translate('drop_off_points.drop_off_point_distance_note', {
        distance: getDistanceToNearestDropOffPoint(),
      })

    return (
      <Cell>
        <Bubble styling={Bubble.Styling.Tight}>
          <Cell
            type={Cell.Type.Navigating}
            prefix={<Icon name={LocationPinMap24} color={Icon.Color.GreyscaleLevel2} />}
            theme="transparent"
            title={title}
            body={body}
            onClick={onDropOffPointOpen}
            testId="generate-label-modal-see-drop-off-points"
          />
        </Bubble>
      </Cell>
    )
  }, [
    isDropOffMapEnabled,
    isHomePickupSelected,
    address,
    nearbyDropOffPoints,
    translate,
    onDropOffPointOpen,
    selectedDropOffPoint,
  ])

  return renderDropOffPoints
}

export default DropOffPoints
